/**
 * shell API namespace — workspace picker + related shell surfaces.
 * Shapes typed from the v4 contract (teivor-build-spec/05-modules/shell.md,
 * 08-ui-spec S1). Paths verified against the staging backend.
 */
import { http } from './client';

/**
 * The per-workspace tile returned by GET /api/shell/picker.
 * - am = only assigned tiles
 * - admin/manager = all workspaces
 * - salesman/compliance_reviewer = empty array
 */
export interface WorkspacePickerView {
  id: string;
  workspaceId: string;
  workspaceName: string;
  /** Banner keys in server-side severity order (highest first). */
  banners: string[];
  /** The highest-severity banner key, or empty string when no banners. */
  topSeverity: string;
  /** Deep-link URL for the top banner, or null when no banners. */
  topBannerDeeplink: string | null;
}

/** Response from POST /api/shell/picker/:workspaceId/enter */
export interface EnterWorkspaceResult {
  workspaceId: string;
  /** True when the acting user is viewing as an AM (workspace context). */
  amPov: boolean;
}

export const shell = {
  /**
   * GET /api/shell/picker?search=<str>
   * Returns the workspace tiles the caller is allowed to see.
   */
  picker: (search?: string) =>
    http.get<WorkspacePickerView[]>(
      '/api/shell/picker',
      search ? { search } : undefined,
    ),

  /**
   * POST /api/shell/picker/:workspaceId/enter
   * Establishes workspace context on the server. Roles: admin/manager/am.
   */
  enterWorkspace: (workspaceId: string) =>
    http.post<EnterWorkspaceResult>(`/api/shell/picker/${workspaceId}/enter`),

  /**
   * POST /api/shell/picker/:workspaceId/request-removal
   * AM-only: request removal from the workspace.
   */
  requestRemoval: (workspaceId: string, reason?: string) =>
    http.post<void>(`/api/shell/picker/${workspaceId}/request-removal`, { reason }),
};
